import { useMemo, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CircleUserIcon from '@watershed/icons/components/CircleUser';
import GroupsIcon from '@watershed/icons/components/Groups';
import GearIcon from '@watershed/icons/components/Gear';
import ArrowNEIcon from '@watershed/icons/components/ArrowNE';
import LockKeyholeIcon from '@watershed/icons/components/LockKeyhole';
import PlugIcon from '@watershed/icons/components/Plug';
import LibraryIcon from '@watershed/icons/components/Library';
import CodeIcon from '@watershed/icons/components/Code';
import ImpersonateIcon from '@watershed/icons/components/Impersonate';
import { IconProps } from '@watershed/icons/Icon';
import { t } from '@lingui/core/macro';
import CommandListPage from '@watershed/shared-frontend/components/CommandPalette/kit/CommandListPage';
import { useCommandPalettePageContext } from '@watershed/shared-frontend/components/CommandPalette/CommandPaletteContext';
import { CommandListItem } from '@watershed/shared-frontend/components/CommandPalette/kit/CommandList';
import { PermissionType } from '@watershed/constants/permissions';
import CommandLoadingSkeleton from './shared/CommandLoadingSkeleton';

import useHasPermission from '../../../../utils/useHasPermission';
import { useViewTransition } from '@watershed/shared-frontend/hooks/useViewTransition';
import {
  createSettingsNavigationGroups,
  type SettingsNavigationGroupType,
  type SettingsNavigationItemType,
} from '../../../settings/v2/data/settingsNavigationData';
import {
  usePermissionsSections,
  type PermissionSectionDef,
} from '../../../settings/v2/permissions/data/permissionsSections';
import { useUserContext } from '../../../../utils/UserContext';

// Helper to get a description based on the item ID
function getItemDescription(id: string): string {
  const descriptions: Record<string, string> = {
    // User management
    users: t({
      message: 'Add, remove, and manage users.',
      context: 'Command palette description for Users settings',
    }),
    roles: t({
      message: 'Configure roles, and their associated permissions and users.',
      context: 'Command palette description for Roles settings',
    }),
    permissions: t({
      message: 'View and manage specific resource and action permissions.',
      context: 'Command palette description for Permissions settings',
    }),

    // Workspace
    finance: t({
      message: 'Manage access to the finance product and features.',
      context: 'Command palette description for Finance settings',
    }),
    preferences: t({
      message: 'Configure workspace settings and preferences',
      context: 'Command palette description for Preferences settings',
    }),
    access: t({
      message: 'Configure SSO, SCIM, and other related security settings.',
      context: 'Command palette description for Access settings',
    }),
    integrations: t({
      message: 'Connect to third-party data sources and tools.',
      context: 'Command palette description for Integrations settings',
    }),
    api: t({
      message: 'Manage API keys and settings.',
      context: 'Command palette description for API settings',
    }),

    // Help
    'learning-hub': t({
      message: 'Access documentation and learning resources.',
      context: 'Command palette description for Learning Hub',
    }),
    support: t({
      message: 'Get help from the Watershed team.',
      context: 'Command palette description for Support',
    }),
    'api-docs': t({
      message: 'View API documentation and reference.',
      context: 'Command palette description for API Docs',
    }),
  };

  //
  return descriptions[id] || '';
}

// Helper function to return the appropriate icon component for an item ID
function getItemIcon(id: string): React.ComponentType<IconProps> {
  const icons: Record<string, React.ComponentType<IconProps>> = {
    // User management
    users: CircleUserIcon,
    roles: GroupsIcon,
    permissions: ImpersonateIcon,

    // Workspace
    finance: LibraryIcon,
    preferences: GearIcon,
    access: LockKeyholeIcon,
    integrations: PlugIcon,
    api: CodeIcon,

    // Help
    'learning-hub': ArrowNEIcon,
    support: ArrowNEIcon,
    'api-docs': ArrowNEIcon,

    // Default/Fallback
    default: GearIcon,
  };

  // Handle permissions section IDs (e.g., 'permissions-carbon-credits')
  if (id.startsWith('permissions-')) {
    return icons['permissions'] || icons['default']; // Use general permissions icon or default
  }

  return icons[id] || icons['default'];
}

// Helper to get relevant keywords for a given item
function getItemKeywords(id: string, groupLabel: string): Array<string> {
  const keywords: Record<string, Array<string>> = {
    // User management
    users: ['invite', 'remove', 'delete', 'edit', 'people', 'accounts'],
    roles: ['create', 'edit', 'delete', 'permissions', 'access'],
    permissions: ['access', 'view', 'edit', 'control', 'manage'],

    // Workspace
    preferences: ['settings', 'configure', 'customize', 'workspace'],
    finance: ['billing', 'payment', 'invoice', 'subscription', 'plan'],
    access: ['security', 'sso', 'scim', 'authentication', 'login', 'identity'],
    integrations: ['connect', 'source', 'data', 'import', 'third-party'],
    api: ['token', 'access', 'key', 'secret', 'integration'],

    // Help
    'learning-hub': ['documentation', 'learn', 'guide', 'tutorial', 'help'],
    support: ['help', 'contact', 'assistance', 'ticket'],
    'api-docs': ['api', 'reference', 'documentation', 'developer'],
  };

  return keywords[id] || [id, groupLabel.toLowerCase()];
}

/**
 * Command palette component for navigating to specific settings pages and sections.
 * Provides searchable access to all settings with appropriate categorization and icons.
 */
export default function SettingsCommandPalettePage() {
  const router = useRouter();
  const palette = useCommandPalettePageContext();
  const hasAdminPermission = useHasPermission(PermissionType.Admin);
  const { canAccessFinance } = useUserContext();
  const startViewTransition = useViewTransition();
  const [isLoading, setIsLoading] = useState(true);

  // Get navigation data
  const settingsNavigationGroups = useMemo(
    () => createSettingsNavigationGroups({ canAccessFinance }),
    [canAccessFinance]
  );

  // Get permissions data
  const permissionsSections = usePermissionsSections();

  // Generate the list of all settings entries
  const settingsEntries = useMemo(() => {
    if (!settingsNavigationGroups || !permissionsSections) {
      return [];
    }
    const entries: Array<CommandListItem<unknown>> = [];

    // Add entries from navigation groups
    settingsNavigationGroups.forEach((group: SettingsNavigationGroupType) => {
      group.items.forEach((item: SettingsNavigationItemType) => {
        const itemId = typeof item.id === 'string' ? item.id : '';
        const itemLabel = item.label || itemId;
        const itemDescription = getItemDescription(itemId);
        const IconComponent = getItemIcon(itemId); // Get the specific icon

        entries.push({
          id: itemId,
          title: itemId,
          group: group.label,
          keywords: getItemKeywords(itemId, group.label),
          render: () => (
            <Stack flexGrow={1} alignItems="center" direction="row" gap={1}>
              <IconComponent sx={{ color: 'text.secondary' }} size={14} />
              <Stack
                flexGrow={1}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="body1">{itemLabel}</Typography>
                <Typography
                  variant="body3"
                  sx={{ color: (theme) => theme.palette.text.secondary }}
                >
                  {itemDescription}
                </Typography>
              </Stack>
            </Stack>
          ),
          onSelect: () => {
            const navigate = () => {
              palette.close();
              void router.push(item.href);
            };
            if (startViewTransition) {
              startViewTransition(navigate);
            } else {
              navigate();
            }
          },
          disabled:
            !hasAdminPermission &&
            ['api', 'finance', 'access', 'integrations'].includes(itemId),
        });
      });
    });

    // Add entries from permissions sections
    permissionsSections.forEach((section: PermissionSectionDef) => {
      const sectionTitle = section.title;
      const sectionDescription =
        section.subtitle ||
        t`Configure ${sectionTitle.toLowerCase()} permissions.`;
      const sectionId = `permissions-${section.id}`;

      // Add a command for the section itself
      entries.push({
        id: sectionId,
        title: sectionTitle,
        group: 'Permissions',
        keywords: [
          'permissions',
          'access',
          'control',
          section.title.toLowerCase(),
        ],
        render: () => (
          <Stack direction="row" spacing={1} alignItems="center" flexGrow={1}>
            <Stack
              flexGrow={1}
              alignItems="center"
              direction="row"
              justifyContent="space-between"
            >
              <Typography variant="body1">{sectionTitle}</Typography>
              <Typography
                variant="body3"
                sx={{ color: (theme) => theme.palette.text.secondary }}
              >
                {sectionDescription}
              </Typography>
            </Stack>
          </Stack>
        ),
        onSelect: () => {
          const navigate = () => {
            palette.close();
            void router.push({
              pathname: '/settings/v2/permissions',
              query: { section: section.id },
            });
          };
          if (startViewTransition) {
            startViewTransition(navigate);
          } else {
            navigate();
          }
        },
        disabled: !hasAdminPermission,
      });

      // For each section, we could also add specific permission items
      // but that might be too detailed for the command palette.
    });

    return entries;
  }, [
    palette,
    router,
    hasAdminPermission,
    settingsNavigationGroups,
    permissionsSections,
    startViewTransition,
  ]);

  // Set loading to false once entries are calculated
  useEffect(() => {
    if (settingsEntries.length > 0) {
      setIsLoading(false);
    }
  }, [settingsEntries]);

  // Render loading skeleton or the command list
  if (isLoading || settingsEntries.length === 0) {
    return <CommandLoadingSkeleton type="list" />;
  }

  return (
    <CommandListPage
      commands={settingsEntries}
      searchPlaceholder="Search settings..."
      searchMode="fuzzy"
      enterActionLabel="Open setting"
    />
  );
}
